// Generic self-heal for sprites created before their texture landed.
//
// Staged loading makes "texture not there yet" a normal state: a scene's own download can
// fail (a deploy restarting the server, a flaky link) and the background loader retries it
// later. Phaser draws such an Image/Sprite with its green `__MISSING` box and never looks
// again. Here every Image/Sprite remembers the key it asked for when it got `__MISSING`
// instead, and `healMissingTextures()` (called by PreloadScene after every batch and by the
// scene loaders after their own runs) swaps the real texture in once it exists — at the size
// the object was given, or at the art's native size if it was never sized.
import Phaser from 'phaser';

const MISSING = '__MISSING';
const wanting = new Set();   // Images/Sprites drawn with __MISSING, with `_wantedTexture` set
let installed = false;

export function installMissingTextureHealer() {
  if (installed) return;
  installed = true;
  for (const Cls of [Phaser.GameObjects.Image, Phaser.GameObjects.Sprite]) {
    const orig = Cls.prototype.setTexture;
    Cls.prototype.setTexture = function (key, frame, ...rest) {
      const r = orig.call(this, key, frame, ...rest);
      if (typeof key === 'string' && key !== MISSING && this.texture?.key === MISSING) {
        this._wantedTexture = key;
        wanting.add(this);
      } else if (this._wantedTexture && this.texture?.key !== MISSING) {
        this._wantedTexture = null;
        wanting.delete(this);
      }
      return r;
    };
  }
}

/** Re-texture every registered object whose wanted key is now in `textures`. */
export function healMissingTextures(textures) {
  for (const obj of [...wanting]) {
    if (!obj.active || !obj.scene) { wanting.delete(obj); continue; }
    const key = obj._wantedTexture;
    if (!key || !textures.exists(key)) continue;
    // Sized with setDisplaySize → keep that size; never sized (scale 1) → the art's own size.
    const sized = obj.scaleX !== 1 || obj.scaleY !== 1;
    const w = obj.displayWidth, h = obj.displayHeight;
    obj.setTexture(key);
    if (sized) obj.setDisplaySize(w, h);
    obj._wantedTexture = null;
    wanting.delete(obj);
  }
}

/** For tests/dev: how many objects are still waiting for their art. */
export function missingTextureCount() { return wanting.size; }
